import React, { createContext, useContext, ReactNode } from 'react';
import { useAuth } from './AuthContext';

interface ApiContextType {
  get: (endpoint: string) => Promise<any>;
  post: (endpoint: string, data?: any) => Promise<any>;
  put: (endpoint: string, data?: any) => Promise<any>;
  delete: (endpoint: string) => Promise<any>;
}

const ApiContext = createContext<ApiContextType | undefined>(undefined);

export const useApi = () => {
  const context = useContext(ApiContext);
  if (context === undefined) {
    throw new Error('useApi must be used within an ApiProvider');
  }
  return context;
};

interface ApiProviderProps {
  children: ReactNode;
}

export const ApiProvider: React.FC<ApiProviderProps> = ({ children }) => {
  const { logout, refreshToken } = useAuth();
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://dev-core.guac.fi';
  const STAGE = import.meta.env.VITE_STAGE || 'dev';

  console.log('API Configuration:', {
    baseUrl: API_BASE_URL,
    stage: STAGE,
    mode: import.meta.env.MODE
  });

  const makeRequest = async (
    endpoint: string,
    options: RequestInit = {}
  ): Promise<any> => {
    // Ensure endpoint starts with /admin for admin dashboard API calls
    // (unless it already starts with /admin to avoid double-prefixing)
    const normalizedEndpoint = endpoint.startsWith('/admin') ? endpoint : `/admin${endpoint}`;

    const config: RequestInit = {
      ...options,
      credentials: 'include', // Include HTTP-only cookies
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    };

    try {
      const response = await fetch(`${API_BASE_URL}${normalizedEndpoint}`, config);

      // Handle unauthorized responses
      if (response.status === 401) {
        // Try to refresh token
        try {
          await refreshToken();
          // Retry the request (token is now in HTTP-only cookie)
          const retryResponse = await fetch(`${API_BASE_URL}${normalizedEndpoint}`, config);
          
          if (!retryResponse.ok) {
            throw new Error(`HTTP error! status: ${retryResponse.status}`);
          }
          
          return await retryResponse.json();
        } catch (refreshError) {
          // Refresh failed, logout user
          logout();
          throw new Error('Session expired. Please login again.');
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const jsonResponse = await response.json();

      // Unwrap the data from the standard API response format { success: true, data: {...} }
      if (jsonResponse.success && jsonResponse.data !== undefined) {
        return jsonResponse.data;
      }

      return jsonResponse;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  };

  const get = (endpoint: string) => makeRequest(endpoint, { method: 'GET' });

  const post = (endpoint: string, data?: any) =>
    makeRequest(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });

  const put = (endpoint: string, data?: any) =>
    makeRequest(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });

  const deleteRequest = (endpoint: string) =>
    makeRequest(endpoint, { method: 'DELETE' });

  const value: ApiContextType = {
    get,
    post,
    put,
    delete: deleteRequest,
  };

  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
};
